import { useLingui } from "@lingui/react/macro";
import { CheckIcon } from "lucide-react";

import { cn } from "@/utils/shadcn-ui";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui-kit/base/dropdown-menu";
import { buttonVariants } from "../../components/ui-kit/button";
import { LocaleFlag } from "../../components/ui-kit/flags/locale-flag";
import { persistLocale } from "../../i18n.react";
import { localeLabel } from "../../languages";
import { clinicalLocales } from "../../locale-sets";
import { location } from "../location";

/** Language picker for the app footer. Persists the choice and reloads so every
 * label — including enum maps resolved through the global `i18n` — reflects the
 * new locale (a live switch would leave those non-subscribed labels stale). */
export function LocaleSwitcher() {
  const { i18n, t } = useLingui();
  const current = i18n.locale;

  const selectLocale = (locale: string) => {
    if (locale === current) return;
    persistLocale(locale);
    // A `?locale=` in the URL outranks the stored choice on startup, so keep it
    // in sync when present; otherwise a plain reload picks up the new locale.
    const url = new URL(location.href);
    if (url.searchParams.has("locale")) {
      url.searchParams.set("locale", locale);
      location.assign(url.toString());
    } else {
      location.reload();
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t`Change language`}
        title={localeLabel(current)}
        className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}
      >
        <LocaleFlag locale={current} />
        {current.toUpperCase()}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {clinicalLocales.map((locale) => (
          <DropdownMenuItem
            key={locale}
            onClick={() => selectLocale(locale)}
            className={locale === current ? "text-brand-primary" : ""}
          >
            <CheckIcon
              className={`size-4 ${locale === current ? "" : "invisible"}`}
            />
            <LocaleFlag locale={locale} />
            {localeLabel(locale)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
